import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { ConnectedPosition, Overlay, OverlayModule, ScrollStrategy } from '@angular/cdk/overlay';

export interface ServiceEquivalenceOption {
  id: number;
  name: string;
  targetKind: 'appointment' | 'assignment' | 'activity';
}

@Component({
  selector: 'app-service-equivalence-selector',
  standalone: true,
  imports: [CommonModule, OverlayModule],
  template: `
    <div class="service-selector">
      <div
        #field
        class="service-selector__field"
        cdkOverlayOrigin
        #overlayOrigin="cdkOverlayOrigin"
        (click)="focusSearch()">
        <span *ngIf="selectedOption() as selected" class="service-selector__selection">
          {{ selected.name }}
        </span>
        <span
          *ngIf="selectedOption() as selected"
          class="service-selector__badge"
          [class.service-selector__badge--appointment]="selected.targetKind === 'appointment'"
          [class.service-selector__badge--assignment]="selected.targetKind === 'assignment'"
          [class.service-selector__badge--activity]="selected.targetKind === 'activity'">
          {{ label(selected.targetKind) }}
        </span>
        <input
          #searchInput
          type="text"
          role="combobox"
          aria-autocomplete="list"
          [attr.aria-expanded]="isOpen"
          [attr.aria-controls]="listboxId"
          [attr.aria-activedescendant]="activeOptionId"
          [attr.aria-label]="placeholder"
          [placeholder]="selectedOption() ? '' : placeholder"
          [value]="searchText"
          autocomplete="off"
          (focus)="openOptions()"
          (input)="updateSearch($event)"
          (keydown)="handleKeydown($event)">
        <span class="service-selector__arrow" aria-hidden="true"></span>
      </div>

      <ng-template
        cdkConnectedOverlay
        [cdkConnectedOverlayOrigin]="overlayOrigin"
        [cdkConnectedOverlayOpen]="isOpen"
        [cdkConnectedOverlayPositions]="positions"
        [cdkConnectedOverlayWidth]="panelWidth"
        [cdkConnectedOverlayScrollStrategy]="scrollStrategy"
        [cdkConnectedOverlayFlexibleDimensions]="true"
        [cdkConnectedOverlayPush]="true"
        [cdkConnectedOverlayViewportMargin]="8"
        (overlayOutsideClick)="closeOptions()">
        <div class="service-selector__panel" [id]="listboxId" role="listbox">
          <button
            *ngFor="let option of filteredOptions(); let index = index; trackBy: trackOption"
            type="button"
            role="option"
            class="service-selector__option"
            [id]="optionId(index)"
            [class.service-selector__option--active]="index === activeIndex"
            [attr.aria-selected]="isSelected(option)"
            (mousedown)="$event.preventDefault()"
            (mouseenter)="activeIndex = index"
            (click)="choose(option)">
            <span class="service-selector__option-name">{{ option.name }}</span>
            <span
              class="service-selector__badge"
              [class.service-selector__badge--appointment]="option.targetKind === 'appointment'"
              [class.service-selector__badge--assignment]="option.targetKind === 'assignment'"
              [class.service-selector__badge--activity]="option.targetKind === 'activity'">
              {{ label(option.targetKind) }}
            </span>
          </button>
          <div *ngIf="!filteredOptions().length" class="service-selector__empty" role="status">
            Sin resultados
          </div>
        </div>
      </ng-template>

      <button
        *ngIf="showCreate"
        type="button"
        class="service-selector__create"
        (click)="create.emit()">
        Crear nuevo
      </button>
    </div>
  `,
  styles: [`
    :host{display:block;width:100%;min-width:0}
    .service-selector{display:flex;align-items:center;gap:8px;width:100%;min-width:0}
    .service-selector__field{box-sizing:border-box;display:flex;align-items:center;gap:6px;min-width:0;min-height:42px;flex:1;padding:0 5px;border-bottom:1px solid #174d78;cursor:text}
    .service-selector__field:focus-within{border-bottom:2px solid #2b7bbb}
    .service-selector__selection{flex:0 1 auto;max-width:60%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;color:#173f66}
    .service-selector__badge{display:inline-flex;align-items:center;flex:none;border-radius:999px;padding:3px 9px;font-size:.73em;font-weight:600;line-height:1.25;white-space:nowrap}
    .service-selector__badge--appointment{background:#c9e3f4;color:#174d78}
    .service-selector__badge--assignment{background:#deeff9;color:#285f88}
    .service-selector__badge--activity{background:#eff8fd;color:#397295}
    .service-selector__field input{box-sizing:border-box;min-width:12px;flex:1;border:0;outline:0;background:transparent;color:#173f66;font:inherit;padding:5px 0}
    .service-selector__field input::placeholder{color:#173f66;opacity:1}
    .service-selector__arrow{width:7px;height:7px;flex:none;margin:0 4px 4px 3px;border-right:1px solid #52718c;border-bottom:1px solid #52718c;transform:rotate(45deg)}
    .service-selector__panel{box-sizing:border-box;max-height:min(328px,calc(100vh - 16px));overflow:auto;padding:4px 0;border-radius:5px;background:#fff;box-shadow:0 5px 18px rgba(0,0,0,.22)}
    .service-selector__option{display:flex;align-items:center;justify-content:space-between;gap:16px;width:100%;min-height:48px;padding:0 18px;border:0;background:#fff;color:#173f66;text-align:left;font:inherit;cursor:pointer}
    .service-selector__option-name{min-width:0;overflow:hidden;text-overflow:ellipsis}
    .service-selector__option:hover,.service-selector__option--active{background:#edf6fb}
    .service-selector__option:focus-visible{outline:2px solid #2b7bbb;outline-offset:-2px}
    .service-selector__empty{padding:14px 18px;color:#607d8b}
    .service-selector__create{flex:none;padding:8px 10px;border:0;background:transparent;color:#4a86ce;font:inherit;white-space:nowrap;cursor:pointer}
    .service-selector__create:hover{text-decoration:underline}
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ServiceEquivalenceSelectorComponent {
  private static nextId = 0;
  private readonly overlay = inject(Overlay);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('field', { read: ElementRef }) private field?: ElementRef<HTMLElement>;
  @ViewChild('searchInput', { read: ElementRef }) private searchInput?: ElementRef<HTMLInputElement>;

  @Input() targetKind = 'appointment';
  @Input() targetId?: number;
  @Input() options: ServiceEquivalenceOption[] = [];
  @Input() showCreate = false;
  @Input() placeholder = 'Seleccione un encargo, nombramiento o colaboración';
  @Output() selected = new EventEmitter<ServiceEquivalenceOption>();
  @Output() create = new EventEmitter<void>();

  readonly listboxId = `service-selector-listbox-${ServiceEquivalenceSelectorComponent.nextId++}`;
  readonly positions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];
  readonly scrollStrategy: ScrollStrategy = this.overlay.scrollStrategies.reposition();

  searchText = '';
  isOpen = false;
  activeIndex = -1;
  panelWidth = 0;

  get activeOptionId(): string | null {
    return this.activeIndex < 0 ? null : this.optionId(this.activeIndex);
  }

  selectedOption(): ServiceEquivalenceOption | undefined {
    return this.options.find(option => option.targetKind === this.targetKind && option.id === this.targetId);
  }

  label(kind: string): string {
    return kind === 'appointment' ? 'Encargo' : kind === 'assignment' ? 'Nombramiento' : 'Colaboración';
  }

  filteredOptions(): ServiceEquivalenceOption[] {
    const query = this.normalize(this.searchText);
    const kindOrder: Record<ServiceEquivalenceOption['targetKind'], number> = {
      appointment: 0,
      assignment: 1,
      activity: 2,
    };

    return this.options
      .filter(option => !query || this.normalize(option.name).includes(query))
      .sort((left, right) => kindOrder[left.targetKind] - kindOrder[right.targetKind]
        || left.name.localeCompare(right.name, 'es', { sensitivity: 'base' }));
  }

  openOptions(): void {
    this.panelWidth = this.field?.nativeElement.getBoundingClientRect().width ?? 0;
    this.isOpen = true;
    this.cdr.markForCheck();
  }

  focusSearch(): void {
    this.searchInput?.nativeElement.focus();
    this.openOptions();
  }

  updateSearch(event: Event): void {
    this.searchText = (event.target as HTMLInputElement).value;
    this.activeIndex = -1;
    this.openOptions();
  }

  handleKeydown(event: KeyboardEvent): void {
    const options = this.filteredOptions();

    if (event.key === 'Tab' && this.isOpen) {
      this.closeOptions();
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!this.isOpen) this.openOptions();
      if (options.length) this.activeIndex = Math.min(this.activeIndex + 1, options.length - 1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!this.isOpen) this.openOptions();
      if (options.length) this.activeIndex = this.activeIndex <= 0 ? options.length - 1 : this.activeIndex - 1;
    } else if (event.key === 'Enter' && this.isOpen && this.activeIndex >= 0) {
      event.preventDefault();
      const option = options[this.activeIndex];
      if (option) this.choose(option);
    } else if (event.key === 'Escape' && this.isOpen) {
      event.preventDefault();
      this.closeOptions();
    }

    this.cdr.markForCheck();
  }

  choose(option: ServiceEquivalenceOption): void {
    this.selected.emit(option);
    this.searchText = '';
    this.activeIndex = -1;
    this.closeOptions();
    this.cdr.markForCheck();
  }

  closeOptions(): void {
    if (!this.isOpen && !this.searchText) return;
    this.isOpen = false;
    this.searchText = '';
    this.activeIndex = -1;
    this.cdr.markForCheck();
  }

  isSelected(option: ServiceEquivalenceOption): boolean {
    return option.targetKind === this.targetKind && option.id === this.targetId;
  }

  optionId(index: number): string {
    return `${this.listboxId}-option-${index}`;
  }

  trackOption(_index: number, option: ServiceEquivalenceOption): string {
    return `${option.targetKind}:${option.id}`;
  }

  private normalize(value: string): string {
    return value.trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
  }
}
